import { motion } from "framer-motion";
import { FlaskConical, AlertTriangle, Microscope } from "lucide-react";

const METHOD = [
  {
    n: "1",
    t: "FORK TWICE EVERY FIFTEEN DAYS",
    d: "The living city's past three days are forked twice from a snapshot: once with the next intervention of a fixed rotation, once with only a null twin (one extra random draw and nothing else). Nobody chooses the instance; the pick is a function of the city's seed and the period.",
  },
  {
    n: "2",
    t: "RUN WITH THE SAME MODELS",
    d: "Both forks run forward with the same two models as the city (Qwen 2.5 7B for words, Jev for decisions), the same code, the same scenario. If the city has not lived the full window yet, the forks run ahead of it, and the page says so.",
  },
  {
    n: "3",
    t: "COMPARE IN DETAIL",
    d: "The comparison reports: the first event that differs; counts of outcomes at 7, 14 and 30 days as \u201cfork minus city\u201d and \u201cbeyond the null twin\u201d; day-by-day welfare aggregates (household cash, arrears, illness, stress, loneliness, belief error) for city, fork and twin; the change's own indicators (for a closure: who was turned away, where people shopped instead; for the subway: shifts missed, nights at the station); who learned of the change and through which channel (at the door, told, read); and the conversations about it, with their lines.",
  },
  {
    n: "4",
    t: "A FIXED CATALOGUE",
    d: "Clinic, grocer, school or library closure; subway outage; rent up or down; grocer price shock; payroll delayed a week; credit union freeze; a group cancelled; an illness wave nobody is told of; and the null twin. No weather.",
  },
  {
    n: "5",
    t: "COUNT ONLY BEYOND THE NOISE",
    d: "A difference counts only beyond what the null twin shows; one twin catches structural effects (zero against non-zero) and cannot grade a difference of ten or twenty percent.",
  },
];

const STATS = [
  { v: "58", k: "residents refused a trip across the river" },
  { v: "37", k: "nights on the station hall seats, by 19 residents" },
  { v: "5", k: "school days lost" },
  { v: "2", k: "wage packets lost" },
  { v: "×2", k: "the town's stress, for two days" },
  { v: "+9", k: "residents ill vs the null twin" },
];

const CAVEATS = [
  "A fork is one possible continuation, not a prediction.",
  "It stops at the city's present (or at the window's end when the forks ran ahead).",
  "Differences the null twin also shows are drift, not the intervention's.",
  "Results describe this model only.",
];

export default function Experiments() {
  return (
    <section id="experiments" className="py-24 sm:py-32 bg-gb-bg2 border-y-2 border-gb-line">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7 }}
          className="mb-12"
        >
          <p className="text-gb-neon text-[11px] tracking-[0.3em] mb-3">06 · THE EXPERIMENTS</p>
          <h2 className="font-title text-6xl sm:text-7xl lg:text-8xl uppercase text-gb-pale leading-[0.85]">
            Forks against
            <br />
            the living city
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* method */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 border-2 border-gb-line bg-gb-bg p-6 sm:p-8"
            data-testid="experiments-method"
          >
            <div className="flex items-center gap-3 mb-7">
              <FlaskConical size={17} className="text-gb-neon" />
              <h3 className="font-title text-2xl sm:text-3xl text-gb-pale">THE METHOD</h3>
            </div>
            <div className="space-y-6">
              {METHOD.map((m) => (
                <div key={m.n} className="flex gap-4" data-testid={`experiments-step-${m.n}`}>
                  <div className="flex flex-col items-center">
                    <span className="font-pixel text-2xl text-gb-neon leading-none">{m.n}</span>
                    <span className="w-px flex-1 bg-gb-line mt-2" />
                  </div>
                  <div className="pb-2">
                    <h4 className="text-[11px] font-bold tracking-[0.2em] text-gb-pale mb-1">{m.t}</h4>
                    <p className="text-[12px] leading-relaxed text-gb-light/85">{m.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* current experiment */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-5"
          >
            <div className="border-2 border-gb-neon/60 bg-gb-card p-6 sm:p-7 shadow-[0_0_40px_rgba(64,240,112,0.12)] h-full flex flex-col" data-testid="experiments-current">
              <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <span className="font-arcade text-[6px] text-gb-bg bg-gb-neon px-2 py-1">CURRENT · AN EXAMPLE</span>
              </div>
              <h3 className="font-title text-3xl sm:text-4xl text-gb-neon glow-text leading-none">
                What if the subway
                <br />
                had stopped?
              </h3>
              <p className="mt-3 text-[11px] tracking-[0.15em] text-gb-accent">
                FROM NOON · FOR TWO DAYS · MEASURED OVER THREE DAYS
              </p>
              <div className="mt-5 grid grid-cols-2 gap-2">
                {STATS.map((s) => (
                  <div key={s.k} className="border border-gb-line bg-gb-bg px-3 py-3">
                    <span className="font-pixel text-2xl text-gb-neon leading-none">{s.v}</span>
                    <p className="mt-1 text-[10px] leading-snug text-gb-light/85">{s.k}</p>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-[11px] leading-relaxed text-gb-light/80">
                A hall full of stranded sleepers is a contact pool. The last
                effect is reported with the caveat of a single null twin.
              </p>
            </div>
          </motion.div>
        </div>

        {/* caveats + framing */}
        <div className="grid lg:grid-cols-12 gap-6 mt-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 border-2 border-gb-line bg-gb-bg p-6"
            data-testid="experiments-caveats"
          >
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle size={16} className="text-gb-neon" />
              <h3 className="font-title text-2xl text-gb-pale">CAVEATS</h3>
            </div>
            <ul className="space-y-2.5">
              {CAVEATS.map((c) => (
                <li key={c} className="text-[12px] leading-relaxed text-gb-light/85 flex gap-2">
                  <span className="text-gb-neon shrink-0">▸</span>
                  {c}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-7 border-2 border-gb-line bg-gb-bg p-6"
            data-testid="experiments-framing"
          >
            <div className="flex items-center gap-3 mb-4">
              <Microscope size={16} className="text-gb-neon" />
              <h3 className="font-title text-2xl text-gb-pale">RESEARCH FRAMING</h3>
            </div>
            <p className="text-[12px] leading-relaxed text-gb-light/85">
              The main question is whether memory, reflection and shared
              culture make residents understand their world better, and
              whether that changes what happens to them, measured as belief
              error against the kernel's truth and as outcomes (wasted trips,
              refusals, missed meals, arrears, care gaps). Four conditions
              switch learning on in steps (rules, memory, memory with
              reflection, with the newspaper). Criteria (primary metric,
              control, threshold) are written in the repository before runs.
              No research claim is made yet.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}